import type { ModalWidth } from '../types';

// ============================================================================
// UTILIDADES DE TEXTO
// ============================================================================

/**
 * Convierte texto a formato slug (URL-friendly)
 *
 * @param text - Texto a convertir
 * @returns Texto en formato slug
 *
 * @example
 * slugify('Mi Modal de Éxito') // 'mi-modal-de-exito'
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '');
}

// ============================================================================
// UTILIDADES DE ESTILOS
// ============================================================================

/**
 * Resuelve la clase CSS para el ancho del modal
 *
 * @param width - Configuración de ancho del modal
 * @returns Clase CSS correspondiente
 *
 * @example
 * resolveWidthClass('lg') // 'sm:w-[579px]'
 * resolveWidthClass(500) // 'sm:w-[500px]'
 */
export function resolveWidthClass(width: ModalWidth): string {
  if (typeof width === 'number') {
    return `sm:w-[${width}px]`;
  }

  switch (width) {
    case 'lg':
      return 'sm:w-[579px]';
    case 'xl':
      return 'sm:w-[720px]';
    case 'xxl':
      return 'sm:w-[975px]';
    case 'default':
    default:
      return 'sm:w-[418px]';
  }
}

// ============================================================================
// UTILIDADES DE TEST ID
// ============================================================================

/**
 * Genera un test ID base para el modal
 *
 * @param testIdProp - Test ID proporcionado por props
 * @param title - Título del modal
 * @param type - Tipo del modal
 * @returns Test ID base generado
 *
 * @example
 * generateBaseTestId(undefined, 'Mi Modal', 'success') // 'modal-mi-modal'
 * generateBaseTestId('custom-modal', undefined, 'error') // 'custom-modal'
 */
export function generateBaseTestId(
  testIdProp?: string,
  title?: string,
  type?: string
): string {
  if (testIdProp) {
    return testIdProp;
  }

  if (title) {
    return `modal-${slugify(title)}`;
  }

  return `modal-${type || 'default'}`;
}
